import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { APP_VERSION } from "../constants";
import styles from "./Sidebar.module.css";

interface SidebarProps {
  isMobileMenuOpen?: boolean;
  closeMobileMenu?: () => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isMobileMenuOpen = false, 
  closeMobileMenu,
  isMobile = false 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLinkClick = () => {
    if (isMobile && closeMobileMenu) {
      closeMobileMenu();
    }
  };

  return (
    <aside className={`${styles.sidebar} ${isMobile ? styles.sidebarMobile : ''} ${isMobileMenuOpen ? styles.sidebarOpen : ''}`}>
      {/* Logo */}
      <div className={styles.logoSection}>
        <div className={styles.logoContainer}>
          <span className={styles.logoIcon} role="img" aria-label="drum">🥁</span>
          <div>
            <h1 className={styles.logoText}>Drumly</h1>
            <span className={styles.logoSubtext}>Dashboard</span>
          </div>
        </div>
        <span className={styles.versionBadge}>v{APP_VERSION}</span>
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navSection}>
          <span className={styles.navSectionLabel}>Main</span>
          <Link 
            to="/dashboard" 
            className={`${styles.navLink} ${isActive('/dashboard') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="dashboard">📊</span>
            <span>Dashboard</span>
          </Link>
          <Link 
            to="/songsterr-parser" 
            className={`${styles.navLink} ${isActive('/songsterr-parser') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="studio">🎵</span>
            <span>Studio</span>
          </Link>
        </div>

        <div className={styles.navSection}>
          <span className={styles.navSectionLabel}>Library</span>
          <Link 
            to="/songsv2" 
            className={`${styles.navLink} ${isActive('/songsv2') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="drum">🥁</span>
            <span>Songs</span>
          </Link>
          <Link 
            to="/beats" 
            className={`${styles.navLink} ${isActive('/beats') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="piano">🎹</span>
            <span>Beats</span>
          </Link>
          <Link 
            to="/song-requests" 
            className={`${styles.navLink} ${isActive('/song-requests') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="notepad">📝</span>
            <span>Requests</span>
          </Link>
        </div>

        <div className={styles.navSection}>
          <span className={styles.navSectionLabel}>Management</span>
          <Link 
            to="/users" 
            className={`${styles.navLink} ${isActive('/users') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="users">👥</span>
            <span>Users</span>
          </Link>
        </div>

        <div className={styles.navSection}>
          <span className={styles.navSectionLabel}>System</span>
           <Link 
             to="/notifications" 
             className={`${styles.navLink} ${isActive('/notifications') ? styles.navLinkActive : ''}`}
             onClick={handleLinkClick}
           >
             <span className={styles.navIcon} role="img" aria-label="notifications">🔔</span>
             <span>Notifications</span>
           </Link>
          <Link 
            to="/about" 
            className={`${styles.navLink} ${isActive('/about') ? styles.navLinkActive : ''}`}
            onClick={handleLinkClick}
          >
            <span className={styles.navIcon} role="img" aria-label="info">ℹ️</span>
            <span>About</span>
          </Link>
        </div>
      </nav>

      {/* Logout Button */}
      <div className={styles.sidebarFooter}>
        <button onClick={handleLogout} className={styles.logoutButton}>
          <span className={styles.navIcon} role="img" aria-label="logout">🚪</span>
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
